import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { authApi, type AuthIdentity } from '@/api/auth'

interface AuthContextValue {
  identity: AuthIdentity | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: {
    email: string
    verification_token: string
    password: string
    display_name: string
    organization_name?: string
    invitation_token?: string
  }) => Promise<void>
  logout: () => Promise<void>
  switchOrganization: (organizationId: number) => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentity] = useState<AuthIdentity | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      setIdentity(await authApi.me())
    } catch {
      setIdentity(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<AuthContextValue>(
    () => ({
      identity,
      loading,
      refresh,
      login: async (email, password) => {
        setIdentity(await authApi.login({ email, password }))
      },
      register: async (data) => {
        setIdentity(await authApi.register(data))
      },
      logout: async () => {
        await authApi.logout()
        setIdentity(null)
      },
      switchOrganization: async (organizationId) => {
        await authApi.switchOrganization(organizationId)
        window.location.assign('/')
      },
    }),
    [identity, loading, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
